import { useState, type ReactNode } from 'react';
import { IconChart, IconTable } from '../icons';

export interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

export function useTip() {
  const [tip, setTip] = useState<TipState | null>(null);
  return { tip, show: setTip, hide: () => setTip(null) };
}

export function Tooltip({ tip, width }: { tip: TipState | null; width: number }) {
  if (!tip) return null;
  const left = Math.min(Math.max(8, tip.x + 14), Math.max(8, width - 240));
  return (
    <div className="tooltip" role="status" style={{ left, top: Math.max(0, tip.y - 10) }}>
      {tip.content}
    </div>
  );
}

/** Rahmen mit Titel, Untertitel und Umschaltung Diagramm/Tabelle. */
export function ChartFrame({
  title,
  sub,
  table,
  children,
  actions,
  id,
  stale,
}: {
  title: ReactNode;
  sub?: ReactNode;
  table?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  id?: string;
  stale?: boolean;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <figure className={`chart-block${stale ? ' stale' : ''}`} style={{ margin: 0 }} aria-labelledby={id ? `${id}-t` : undefined}>
      <div className="chart-toolbar">
        <figcaption>
          <div className="chart-title" id={id ? `${id}-t` : undefined}>
            {title}
          </div>
          {sub && <div className="chart-sub">{sub}</div>}
        </figcaption>
        <div className="row" style={{ gap: 4 }}>
          {actions}
          {table && (
            <button type="button" className="btn btn-ghost btn-sm" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
              {asTable ? <IconChart /> : <IconTable />}
              {asTable ? 'Diagramm' : 'Tabelle'}
            </button>
          )}
        </div>
      </div>
      {asTable ? table : children}
    </figure>
  );
}

export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];
  const span = max - min;
  const step0 = span / count;
  const mag = 10 ** Math.floor(Math.log10(step0));
  const err = step0 / mag;
  const step = (err >= 7.5 ? 10 : err >= 3.5 ? 5 : err >= 1.5 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-9; v += step) out.push(Math.round(v / step) * step);
  return out;
}

export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const t = niceTicks(0, v * 1.08, 5);
  return t[t.length - 1] >= v ? t[t.length - 1] : v * 1.1;
}

/** Schraffurmuster für Teilwerte und ungültige Bereiche. */
export function Patterns({ id }: { id: string }) {
  return (
    <defs>
      <pattern id={`${id}-hatch`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="5" height="5" fill="#ffffff" />
        <line x1="0" y1="0" x2="0" y2="5" stroke="#9ca3af" strokeWidth="1.6" />
      </pattern>
      <pattern id={`${id}-gap`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="#d1d5db" strokeWidth="1.2" />
      </pattern>
    </defs>
  );
}
